import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Flex,
  SimpleGrid,
  Text,
  Badge,
  VStack,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ViewCouponData } from 'reactRedux/thunks/couponThunk/couponThunk';
import { ListOrderData } from 'reactRedux/thunks/orderThunk/orderThunk';
import CommonTable from 'components/commonTable/CommonTable';
import CommonPagination from 'components/commonPagination/CommonPagination';
import PageLoader from 'components/wholePageLoader/PageLoader';
import { FaEdit } from 'react-icons/fa';
import BackButton from 'components/backButton/BackButton';
import moment from 'moment';
import {
  sideBarRoutes,
  paymentStatusMap,
  paymentStatusColors,
} from 'utils/StaticData';

export const getCouponTypeDisplay = (type) => {
  const typeMap = {
    percentage: 'Percentage',
    fixed: 'Fixed Amount',
    time: 'Minutes',
  };
  return typeMap[type] || type;
};
const CouponDetail = () => {
  const { couponDetailId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { viewCouponDetails, isViewLoading } = useSelector(
    (state) => state.CouponSlice,
  );
  const { adminDetails } = useSelector((state) => state.authSlice);

  useEffect(() => {
    if (couponDetailId) {
      dispatch(ViewCouponData(couponDetailId));
    }
  }, [dispatch, couponDetailId]);

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    // Parse the date without timezone conversion to maintain the exact date
    return moment.parseZone(dateString).format('DD-MM-YYYY');
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return (
        <Badge colorScheme="green" variant="solid" fontSize="sm">
          Active
        </Badge>
      );
    }
    return (
      <Badge colorScheme="gray" variant="solid" fontSize="sm">
        Inactive
      </Badge>
    );
  };

  const getPaymentStatusBadge = (status) => {
    return (
      <Text
        color={paymentStatusColors[status] || 'gray.500'}
        fontSize="sm"
        fontWeight="600"
        align="left"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
      >
        {paymentStatusMap[status] || 'Unknown'}
      </Text>
    );
  };

  const getCouponValueDisplay = (type, value, symbol = '€') => {
    if (!value) return 'N/A';

    switch (type) {
      case 'percentage':
        return `${value}%`;
      case 'fixed':
        return `${symbol}${value}`;
      case 'time':
        return `${value} minutes`;
      default:
        return value;
    }
  };

  // Check if user has permission to edit (only Admin and Dealer)
  const canEdit =
    adminDetails?.role === sideBarRoutes?.Admin ||
    adminDetails?.role === sideBarRoutes?.Dealer;

  // Orders that used this coupon
  const [orderPage, setOrderPage] = useState(1);
  const { orderListData, isLoading: isOrderLoading } = useSelector(
    (state) => state.OrderSlice,
  );

  useEffect(() => {
    if (couponDetailId) {
      dispatch(
        ListOrderData({
          page: orderPage,
          limit: 10,
          couponId: Number(couponDetailId),
          sortKey: 'rentTime',
          sortBy: -1,
        }),
      );
    }
  }, [dispatch, couponDetailId, orderPage]);
  // Refresh coupon details when order list finishes loading to keep totalDiscountGiven fresh
  useEffect(() => {
    if (!isOrderLoading && couponDetailId) {
      dispatch(ViewCouponData(couponDetailId));
    }
  }, [isOrderLoading, couponDetailId, dispatch]);
  const columnHelper = createColumnHelper();
  const orderColumns = [
    columnHelper.accessor((row) => row?.intentId, {
      id: 'intentId',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Order Id
        </Text>
      ),
      cell: (info) => (
        <Text
          color="gray.700"
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.rentTime, {
      id: 'rentTime',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Date
        </Text>
      ),
      cell: (info) => (
        <Text
          color="gray.700"
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {info.getValue()
            ? moment.parseZone(info.getValue()).format('DD-MM-YYYY HH:mm:ss')
            : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.rentalMerchant, {
      id: 'rentalMerchant',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Merchant
        </Text>
      ),
      cell: (info) => (
        <Text
          color="gray.700"
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.paymentStatus, {
      id: 'paymentStatus',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Payment Status
        </Text>
      ),
      cell: (info) => getPaymentStatusBadge(info.getValue()),
    }),
    columnHelper.accessor((row) => row?.last4, {
      id: 'last4',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Last 4
        </Text>
      ),
      cell: (info) => (
        <Text
          color="gray.700"
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {info.getValue()}
        </Text>
      ),
    }),
  ];

  if (isViewLoading) {
    return <PageLoader />;
  }

  const couponData = viewCouponDetails?.data || viewCouponDetails || {};

  return (
    <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
      {/* Back button and Edit Button */}
      <Flex justifyContent="space-between" mb={4} gap="3">
        <BackButton fallbackUrl="/admin/coupons" label="Back to Coupons" />
        {canEdit && (
          <Flex gap="3">
            <Button
              py="10px"
              px="15px"
              fontWeight="semibold"
              borderRadius="8px"
              bg="primary"
              color="white"
              _hover={{ bg: 'primary', opacity: 0.8 }}
              type="button"
              leftIcon={<FaEdit size={22} />}
              onClick={() =>
                navigate(`/admin/coupons/edit/${couponData?.couponId}`)
              }
            >
              Edit Coupon
            </Button>
          </Flex>
        )}
      </Flex>

      {/* Main Details Card */}
      <Card
        w="100%"
        boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
        borderRadius="20px"
        paddingInline="40px"
        paddingBlock="40px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
        mb={6}
      >
        <Text fontSize="2xl" fontWeight="bold" mb={6}>
          Coupon Details
        </Text>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {/* Coupon Code */}
          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="150px">
              Coupon Code :
            </Text>
            <Text fontWeight="semibold" fontSize="lg">
              {couponData?.couponCode || 'N/A'}
            </Text>
          </Flex>

          {/* Status */}
          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="150px">
              Status :
            </Text>
            {getStatusBadge(couponData?.isActive)}
          </Flex>

          {/* Coupon Type */}
          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="150px">
              Coupon Type :
            </Text>
            <Text fontWeight="semibold">
              {getCouponTypeDisplay(couponData?.couponType)}
            </Text>
          </Flex>

          {/* Coupon Value */}
          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="150px">
              Coupon Value :
            </Text>
            <Text fontWeight="semibold">
              {getCouponValueDisplay(
                couponData?.couponType,
                couponData?.couponValue,
                couponData?.region?.currencySymbol ?? '€',
              )}
            </Text>
          </Flex>

          {/* Start Date */}
          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="150px">
              Valid From :
            </Text>
            <Text fontWeight="semibold">
              {formatDateForDisplay(couponData?.startDate)}
            </Text>
          </Flex>

          {/* End Date */}
          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="150px">
              Valid Until :
            </Text>
            <Text fontWeight="semibold">
              {formatDateForDisplay(couponData?.endDate)}
            </Text>
          </Flex>

          {/* Max Users */}
          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="150px">
              Maximum Users :
            </Text>
            <Text fontWeight="semibold">
              {couponData?.maxUsers || 'Unlimited'}
            </Text>
          </Flex>

          {/* Max Uses Per User */}
          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="150px">
              Max Uses Per User :
            </Text>
            <Text fontWeight="semibold">
              {couponData?.maxUsesPerUser || 'Unlimited'}
            </Text>
          </Flex>

          {/* Max Uses Per Day */}
          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="150px">
              Daily Limit :
            </Text>
            <Text fontWeight="semibold">
              {couponData?.dailyLimit || 'Unlimited'}
            </Text>
          </Flex>

          {/* Usage Count */}
          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="150px">
              Current Usage :
            </Text>
            <Text fontWeight="semibold">
              {couponData?.usageCount || 0}
              {couponData?.maxUsers && ` / ${couponData?.maxUsers}`}
            </Text>
          </Flex>

          {/* region */}
          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="150px">
              Region :
            </Text>
            <Text fontWeight="semibold" fontSize="lg">
              {couponData?.region?.regionName || 'N/A'}
            </Text>
          </Flex>

          {/* Is Highlight */}
          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="150px">
              Is Highlight :
            </Text>
            <Text fontWeight="semibold">
              {couponData?.isHighlight ? 'Yes' : 'No'}
            </Text>
          </Flex>

          {/* Is Highlight Price */}
          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="150px">
              Highlight Price :
            </Text>
            <Text fontWeight="semibold">
              {getCouponValueDisplay('fixed', couponData?.highlightPrice || 0)}
            </Text>
          </Flex>

          {/* Is Highlight Label */}
          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="150px">
              Highlight Label :
            </Text>
            <Text fontWeight="semibold">
              {couponData?.highlightLabel || 'N/A'}
            </Text>
          </Flex>
        </SimpleGrid>

        {/* Display Text */}
        <Flex
          alignItems={{ sm: 'start', md: 'center' }}
          direction={{ sm: 'column', md: 'row' }}
          gap="10px"
          padding="4px 8px"
          marginTop={4}
          borderLeft={'1px solid #ccc'}
        >
          <Text fontWeight="normal" width="150px">
            Display Text :
          </Text>
          <Text fontWeight="semibold" fontSize="lg">
            {couponData?.displayText || 'N/A'}
          </Text>
        </Flex>

        {/* Description */}
        <Flex
          alignItems={{ sm: 'start', md: 'center' }}
          direction={{ sm: 'column', md: 'row' }}
          gap="10px"
          padding="4px 8px"
          marginTop={4}
          borderLeft={'1px solid #ccc'}
        >
          <Text fontWeight="normal" width="150px">
            Description :
          </Text>
          <Text fontWeight="semibold" fontSize="lg">
            {couponData?.description || 'N/A'}
          </Text>
        </Flex>
      </Card>

      {/* Merchants Card */}
      {couponData?.merchants && couponData?.merchants.length > 0 && (
        <Card
          w="100%"
          boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
          borderRadius="20px"
          paddingInline="40px"
          paddingBlock="30px"
          mb={6}
        >
          <Text fontSize="xl" fontWeight="bold" mb={4}>
            Associated Merchants
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {couponData.merchants.map((merchant, index) => (
              <Box
                key={merchant.merchantId || index}
                p={3}
                borderWidth="1px"
                borderRadius="md"
                borderColor="gray.200"
              >
                <VStack align="start" spacing={1}>
                  <Text fontWeight="semibold">
                    {merchant.name || `Merchant ${merchant.merchantId}`}
                  </Text>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </Card>
      )}

      {/* Users Card */}
      {couponData?.users && couponData?.users.length > 0 && (
        <Card
          w="100%"
          boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
          borderRadius="20px"
          paddingInline="40px"
          paddingBlock="30px"
          mb={6}
        >
          <Text fontSize="xl" fontWeight="bold" mb={4}>
            Associated Users
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {couponData.users.map((user, index) => (
              <Box
                key={user.userId || index}
                p={3}
                borderWidth="1px"
                borderRadius="md"
                borderColor="gray.200"
              >
                <VStack align="start" spacing={1}>
                  <Text fontWeight="semibold">
                    {`${user.firstName || ''} ${user.lastName || ''}`.trim() ||
                      `User ${user.userId}`}
                  </Text>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </Card>
      )}

      {/* Orders Using This Coupon */}
      <Card
        w="100%"
        boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
        borderRadius="20px"
        paddingInline="40px"
        paddingBlock="30px"
        mb={6}
      >
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="xl" fontWeight="bold">
            Orders Using This Coupon
          </Text>
          <Text fontSize="lg" fontWeight="bold">
            Total Discount Given:{' '}
            {typeof couponData?.totalDiscountGiven === 'number'
              ? `${couponData?.region?.currencySymbol ?? '€'}${couponData.totalDiscountGiven.toFixed(2)}`
              : `${couponData?.region?.currencySymbol ?? '€'}${couponData?.totalDiscountGiven || '0.00'}`}
          </Text>
        </Flex>

        <CommonTable
          columns={orderColumns}
          data={orderListData?.list || []}
          isLoading={isOrderLoading}
          isLink={true}
          parentLink="/admin/orders/details"
          keyValue="orderId"
          rowclick={[]}
          dynamicWidth={{
            intentId: '200px',
            rentTime: '200px',
            rentalMerchant: '200px',
            paymentStatus: '120px',
            last4: '100px',
          }}
        />

        {orderListData?.total > (orderListData?.limit || 10) && (
          <CommonPagination
            totalCount={orderListData?.total}
            activePage={orderPage}
            pageCount={Math.ceil(
              orderListData?.total / (orderListData?.limit || 10),
            )}
            onPageChange={(e) => setOrderPage(e)}
            itemsPerPage={orderListData?.limit || 10}
          />
        )}
      </Card>
    </Box>
  );
};

export default CouponDetail;
